import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Snackbar,
  Typography,
} from "@mui/material";
import { CheckCircle, Cancel } from "@mui/icons-material";
import { useState } from "react";
import apiClient from "@/api/client";
import { brand } from "@/theme";

interface Suggestion {
  id: string;
  article_id: string | null;
  suggestion_type: string;
  status: string;
  suggested_by_agent: string;
  title: string | null;
  content: string | null;
  reason: string | null;
  article_title: string | null;
  created_at: string;
  reviewed_at: string | null;
}

const TYPE_LABELS: Record<string, string> = {
  new_article: "New Article",
  edit: "Edit",
  tag_change: "Tag Change",
  archive: "Archive",
};

export default function SuggestionReview() {
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const { data, isLoading } = useQuery<Suggestion[]>({
    queryKey: ["suggestions"],
    queryFn: async () => {
      const res = await apiClient.get<{ suggestions: Suggestion[] }>("/suggestions");
      return res.data.suggestions;
    },
  });

  const reviewMutation = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: string }) => {
      await apiClient.post(`/suggestions/${id}/review`, { action });
    },
    onSuccess: (_, { action }) => {
      queryClient.invalidateQueries({ queryKey: ["suggestions"] });
      setSnackbar({ open: true, message: `Suggestion ${action}` });
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const pending = data?.filter((s) => s.status === "pending") ?? [];
  const reviewed = data?.filter((s) => s.status !== "pending") ?? [];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Suggestion Review
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Agents suggest KB changes here. Review and approve or reject them.
      </Typography>

      {/* Pending */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        Pending ({pending.length})
      </Typography>
      {pending.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No pending suggestions. Agents will create them during heartbeats.
        </Alert>
      )}
      {pending.map((s) => (
        <Card key={s.id} sx={{ mb: 2, borderLeft: `4px solid ${brand.warmGold}` }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Chip
                label={TYPE_LABELS[s.suggestion_type] ?? s.suggestion_type}
                size="small"
                color="warning"
              />
              <Chip label={s.suggested_by_agent} size="small" variant="outlined" />
              <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                {new Date(s.created_at).toLocaleString()}
              </Typography>
            </Box>
            {s.title && (
              <Typography variant="subtitle1" fontWeight={600}>
                {s.title}
              </Typography>
            )}
            {s.article_title && (
              <Typography variant="body2" color="text.secondary">
                Article: {s.article_title}
              </Typography>
            )}
            {s.reason && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {s.reason}
              </Typography>
            )}
            {s.content && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5, fontStyle: "italic" }}
              >
                {s.content.slice(0, 200)}
                {s.content.length > 200 ? "..." : ""}
              </Typography>
            )}
            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<CheckCircle />}
                onClick={() => reviewMutation.mutate({ id: s.id, action: "approved" })}
                disabled={reviewMutation.isPending}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<Cancel />}
                onClick={() => reviewMutation.mutate({ id: s.id, action: "rejected" })}
                disabled={reviewMutation.isPending}
              >
                Reject
              </Button>
            </Box>
          </CardContent>
        </Card>
      ))}

      {/* Reviewed */}
      {reviewed.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
            Reviewed ({reviewed.length})
          </Typography>
          {reviewed.map((s) => (
            <Card key={s.id} sx={{ mb: 1, opacity: 0.7 }}>
              <CardContent sx={{ py: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    label={s.status}
                    size="small"
                    color={s.status === "approved" ? "success" : "error"}
                  />
                  <Chip
                    label={TYPE_LABELS[s.suggestion_type] ?? s.suggestion_type}
                    size="small"
                    variant="outlined"
                  />
                  <Typography variant="body2">
                    {s.title || s.article_title || "Untitled"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                    by {s.suggested_by_agent} ·{" "}
                    {s.reviewed_at ? new Date(s.reviewed_at).toLocaleDateString() : ""}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        message={snackbar.message}
      />
    </Box>
  );
}
